import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

interface MultiCheckListProps {
  options: { value: string; label: string; hint?: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  /** Show a search box above the list when there are many options. */
  searchable?: boolean;
  placeholder?: string;
  emptyLabel?: string;
}

/**
 * Searchable, scrollable checkbox list for a large option set (e.g. the
 * LLM tool catalog). Selected values are kept as an array; order matches
 * the incoming `selected` for stability.
 */
export function MultiCheckList({
  options,
  selected,
  onChange,
  disabled,
  searchable = true,
  placeholder,
  emptyLabel,
}: MultiCheckListProps) {
  const { t } = useTranslation("common");
  const [query, setQuery] = React.useState("");
  const selectedSet = new Set(selected);

  const toggle = (value: string) => {
    onChange(
      selectedSet.has(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter(
        (o) =>
          o.value.toLowerCase().includes(q) ||
          o.label.toLowerCase().includes(q) ||
          (o.hint ?? "").toLowerCase().includes(q)
      )
    : options;

  return (
    <div className="space-y-2">
      {searchable && options.length > 8 && (
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder ?? t("fields.multiCheck.search")}
          disabled={disabled}
          className="h-9"
        />
      )}
      <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-input p-2">
        {filtered.length === 0 ? (
          <p className="px-1 py-2 text-sm text-muted-foreground">{emptyLabel ?? t("fields.multiCheck.empty")}</p>
        ) : (
          filtered.map((opt) => {
            const active = selectedSet.has(opt.value);
            return (
              <label
                key={opt.value}
                className={cn(
                  "flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted",
                  disabled && "cursor-not-allowed opacity-50"
                )}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4"
                  checked={active}
                  onChange={() => toggle(opt.value)}
                  disabled={disabled}
                />
                <span className="min-w-0">
                  <span className="font-mono text-xs">{opt.value}</span>
                  {opt.hint && (
                    <span className="ml-2 text-muted-foreground">{opt.hint}</span>
                  )}
                </span>
              </label>
            );
          })
        )}
      </div>
      {selected.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t("fields.multiCheck.selected", { count: selected.length })}
        </p>
      )}
    </div>
  );
}
